<script setup lang="ts">
/**
 * Home, the command center: what needs you (approvals, inbox, your todos,
 * knowledge decisions) with the numbers on top, what happened overnight,
 * what Schorschi sees, quick ways in, and what you can do.
 *
 * Two layouts, so that reading and keyboard order always match what you see:
 * from xl a main column and a side column (Schorschi, quick actions, budget,
 * setup); narrower, one column ordered by what matters first (what needs
 * you, Schorschi and quick actions, then the rest), pairs side by side from
 * md up. A pair whose card hides itself leaves the other the whole row.
 */
import { Sparkles } from "@lucide/vue";
import { useMediaQuery } from "@vueuse/core";
import { computed } from "vue";
import PageHeader from "@/components/common/PageHeader.vue";
import AiBudgetCard from "@/components/home/AiBudgetCard.vue";
import CapabilitiesCard from "@/components/home/CapabilitiesCard.vue";
import ClaudeSetupCard from "@/components/home/ClaudeSetupCard.vue";
import ConnectionsCard from "@/components/home/ConnectionsCard.vue";
import GetStartedCard from "@/components/home/GetStartedCard.vue";
import HomeStatusStrip from "@/components/home/HomeStatusStrip.vue";
import NeedsYouCard from "@/components/home/NeedsYouCard.vue";
import QuickActionsCard from "@/components/home/QuickActionsCard.vue";
import RecentWorkCards from "@/components/home/RecentWorkCards.vue";
import SchorschiCard from "@/components/home/SchorschiCard.vue";
import { Button } from "@/components/ui/button";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Home", layout: "wide" } });

const { displayName, can } = useSession();

const greeting = computed(() => {
  const h = new Date().getHours();
  return h < 12 ? t.home.greetingMorning : h < 18 ? t.home.greetingAfternoon : t.home.greetingEvening;
});
const firstName = computed(() => {
  const first = displayName.value.split(/[\s.]/)[0] ?? "";
  return first.charAt(0).toUpperCase() + first.slice(1);
});
const today = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(new Date());

/** xl (80rem): two columns. */
const twoColumns = useMediaQuery("(min-width: 80rem)");
/** Two cards side by side from md up; auto-fit gives a lone card the whole row. */
const PAIR = "grid min-w-0 items-start gap-6 md:grid-cols-[repeat(auto-fit,minmax(18rem,1fr))]";
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader :eyebrow="today" :title="firstName ? `${greeting}, ${firstName}` : greeting" :description="t.home.intro">
      <template #actions>
        <Button v-if="can('knowledge.read')" as-child><RouterLink to="/ask"><Sparkles /> {{ t.home.quick.ask }}</RouterLink></Button>
      </template>
    </PageHeader>

    <HomeStatusStrip />

    <div v-if="twoColumns" class="grid grid-cols-[minmax(0,1fr)_22rem] items-start gap-6 2xl:grid-cols-[minmax(0,1fr)_26rem]">
      <!-- What needs you, what happened, what you can do -->
      <div class="flex min-w-0 flex-col gap-6">
        <NeedsYouCard />
        <GetStartedCard />
        <RecentWorkCards />
        <CapabilitiesCard />
      </div>
      <!-- Side column: Schorschi, quick ways in, budget, setup -->
      <div class="flex min-w-0 flex-col gap-6">
        <SchorschiCard v-if="can('schorschi.read')" />
        <QuickActionsCard />
        <AiBudgetCard />
        <ConnectionsCard />
        <ClaudeSetupCard />
      </div>
    </div>

    <template v-else>
      <NeedsYouCard />
      <div :class="PAIR">
        <SchorschiCard v-if="can('schorschi.read')" />
        <QuickActionsCard />
      </div>
      <GetStartedCard />
      <RecentWorkCards />
      <CapabilitiesCard />
      <div :class="PAIR">
        <AiBudgetCard />
        <ConnectionsCard />
      </div>
      <ClaudeSetupCard />
    </template>
  </div>
</template>
